import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { TouchableWithoutFeedback } from "react-native";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { feedback__AreaType, gql } from "@medbillai/graphql-types";
import { isWeb, SubmitButton, YStack } from "@medbillai/ui";
import { AdaptiveDialogSheet } from "../../../components/AdaptiveDialogSheet";
import { analyticsService } from "../../../lib/analytics";
import { errorHandling } from "../../../lib/apollo/utils";
import { getIssueQuery } from "../../case/utils/queries";
import AdditionalInfo from "../../feedback/components/AdditionalInfo";
import CaseRatingCard from "../../feedback/components/CaseRatingCard";
import OptionsGroup from "../../feedback/components/OptionsGroup";
import type { FeedbackOption } from "../../feedback/types";
import { getMostRecentClosedIssueWithoutFeedbackQuery } from "../../home/utils/queries";
const getFeedbackOptionsQuery = gql(/* GraphQL */`
  query getFeedbackOptions($area: feedback__AreaType!) {
    feedback__Option(
      filter: { area: { eq: $area } }
      order: { order: { dir: ASC } }
    ) {
      sentiment
      value
      id
    }
  }
`);
const createIssueFeedbackMutation = gql(/* GraphQL */`
  mutation CreateIssueFeedback($input: CreateIssueFeedbackInput!) {
    createIssueFeedback(input: $input) {
      success
    }
  }
`);
type FeedbackDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  rating?: number;
  onRatingChange: (rating: number | undefined) => void;
  submitted?: boolean;
  onFeedbackSubmitted?: () => void;
};

/**
 Make entire view pressable so that it can trigger scroll. On web, the
 TouchableWithoutFeedback element blows up, but the scrolling problems were only
 on mobile, so it all works out.

 https://github.com/tamagui/tamagui/issues/2746
*/
function MaybeTouchable({
  children
}: {
  children: React.ReactNode;
}) {
  return isWeb ? children : <TouchableWithoutFeedback onPress={() => {}} data-sentry-element="TouchableWithoutFeedback" data-sentry-component="MaybeTouchable" data-sentry-source-file="FeedbackDialog.tsx">
      {children}
    </TouchableWithoutFeedback>;
}
export function FeedbackDialog({
  open,
  onOpenChange,
  issueId,
  rating,
  onRatingChange,
  submitted = false,
  onFeedbackSubmitted
}: FeedbackDialogProps) {
  const [reviewText, setReviewText] = useState("");
  const [selectedOptionIds, setSelectedOptionIds] = useState(new Set<string>());
  const [errorMessage, setErrorMessage] = useState("");
  const {
    data: optionsData
  } = useQuery(getFeedbackOptionsQuery, {
    variables: {
      area: feedback__AreaType.ISSUE
    },
    context: errorHandling("error-screen"),
    skip: !open
  });
  // The backend automatically stitches this feedback to the most recent ClosedEvent
  // for the issue that doesn't already have feedback associated with it.
  // This happens in feedback.service.ts createUserIssueFeedback method (lines 105-135)
  // which finds the most recent closed event without feedback and links them together.
  const [createIssueFeedback, {
    loading: submitting
  }] = useMutation(createIssueFeedbackMutation, {
    context: errorHandling("error-screen"),
    refetchQueries: [getMostRecentClosedIssueWithoutFeedbackQuery, getIssueQuery]
  });
  const onPressOption = (option: FeedbackOption) => {
    setSelectedOptionIds(prev => {
      const updatedOptions = new Set(prev);
      if (prev.has(option.id)) {
        updatedOptions.delete(option.id);
      } else {
        updatedOptions.add(option.id);
      }
      return updatedOptions;
    });
  };
  const onSubmitFeedback = async () => {
    if (!rating || submitted) {
      return;
    }
    const result = await createIssueFeedback({
      variables: {
        input: {
          issueId,
          rating,
          review: reviewText,
          selectedOptionIds: Array.from(selectedOptionIds)
        }
      }
    });
    if (!result.data?.createIssueFeedback?.success) {
      setErrorMessage("Failed to submit feedback. Please try again.");
      return;
    }
    onFeedbackSubmitted?.();
    analyticsService.trackEvent("FeedbackProvided", {
      rating
    });

    // Close dialog and reset state
    onOpenChange(false);
    onRatingChange(undefined);
    setReviewText("");
    setSelectedOptionIds(new Set());
    setErrorMessage("");
  };
  const content = <KeyboardAvoidingView behavior="position" keyboardVerticalOffset={120}>
      <CaseRatingCard rating={rating} onChangeRating={onRatingChange} submitted={submitted} />

      <YStack padding="$6" pt="$4" gap="$4">
        {optionsData?.feedback__Option?.length ? <OptionsGroup key="options" options={optionsData.feedback__Option} selectedOptionIds={selectedOptionIds} onPressOption={onPressOption} rating={rating} /> : null}

        <AdditionalInfo text={reviewText} onChangeText={setReviewText} errorMessage={errorMessage} />

        <SubmitButton loading={submitting} disabled={!rating || submitting} onPress={onSubmitFeedback}>
          Submit
        </SubmitButton>
      </YStack>
    </KeyboardAvoidingView>;
  return <AdaptiveDialogSheet open={open} onOpenChange={onOpenChange} title="How did we do?" snapPointsMode="percent" snapPoints={[90]} hideTitle={true} contentWrapperProps={{
    gap: "$0",
    px: "$0"
  }} scrollViewProps={{
    px: "$0"
  }} disableScroll={false}
  // The 'drag-to-dismiss' gesture competes for input with the scroll view,
  // so we disable it. https://github.com/tamagui/tamagui/issues/3427
  // Some responses in the issue mention using Sheet.ScrollView instead of
  // ScrollView, but that didn't fix this.
  sheetProps={{
    disableDrag: true
  }} dialogContentProps={{
    maxWidth: 800
  }} absolutePositionTitle={!isWeb} data-sentry-element="AdaptiveDialogSheet" data-sentry-component="FeedbackDialog" data-sentry-source-file="FeedbackDialog.tsx">
      <MaybeTouchable data-sentry-element="MaybeTouchable" data-sentry-source-file="FeedbackDialog.tsx">{content}</MaybeTouchable>
    </AdaptiveDialogSheet>;
}